import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import useStyles from '../styles';
import { AlertSchema } from '../../../requests/api/apiTypes';
import apiClient from '../../../requests/api/apiClient';
import { FetchError } from '../../../views/types';
import AlertModal from '../AlertModal/AlertModal';
import { AlertModalMode } from '../AlertModal/constants';
import AlertsTable from './AlertsTable';
import { ReduxState } from '../../../types';

interface UnifiedAlertsTableProps {
  headers: any[];
  sectorId?: string;
  chartName?: string;
  extraColumns?: (alert: AlertSchema) => Record<string, React.ReactNode>;
  refreshCounter?: number;
}

const ROWS_PER_PAGE = 5;

const AlertsTab: React.FC<UnifiedAlertsTableProps> = (
  {
    headers, extraColumns, sectorId, chartName, refreshCounter: externalRefreshCounter,
  },
) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const selectedFarm = useSelector((state: ReduxState) => state.accountFarm.selectedFarm);
  const [selectedAlert, setSelectedAlert] = useState<AlertSchema | null>(null);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<AlertModalMode>(AlertModalMode.CREATE);
  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alerts, setAlerts] = useState<AlertSchema[]>([]);
  const [paginationParameters, setPaginationParameters] = useState({
    page: 0,
    rowsPerPage: ROWS_PER_PAGE,
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [totalAlerts, setTotalAlerts] = useState(0);

  const loadAlerts = async () => {
    setAlertsLoading(true);
    try {
      // Use selected farm from navbar as the only source
      const farmIdFromQuery = selectedFarm?.id;
      const { data } = await apiClient.alert.alertList(
        paginationParameters.page.toString(),
        paginationParameters.rowsPerPage.toString(),
        sectorId,
        chartName,
        farmIdFromQuery || undefined,
      );
      setAlerts(data.alerts);
      setTotalAlerts(data.totalAlerts);
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setAlertsLoading(false);
    }
  };

  useEffect(() => {
    loadAlerts();
  }, [
    sectorId,
    chartName,
    refreshCounter,
    externalRefreshCounter,
    paginationParameters.page,
    paginationParameters.rowsPerPage,
    selectedFarm?.id,
  ]);

  // Reset to first page when external refresh counter changes (new alert created)
  useEffect(() => {
    if (externalRefreshCounter && externalRefreshCounter > 0) {
      setPaginationParameters((prev) => ({ ...prev, page: 0 }));
    }
  }, [externalRefreshCounter]);

  const handleEditClick = (alert: AlertSchema) => {
    setSelectedAlert(alert);
    setModalMode(AlertModalMode.UPDATE);
    setAlertModalOpen(true);
  };

  const handleViewClick = (alert: AlertSchema) => {
    setSelectedAlert(alert);
    setModalMode(AlertModalMode.VIEW);
    setAlertModalOpen(true);
  };

  const handleModalClose = () => {
    setAlertModalOpen(false);
    setSelectedAlert(null);
    setModalMode(AlertModalMode.CREATE);
  };

  const handleDeleteConfirm = async (alert: AlertSchema) => {
    try {
      await apiClient.alert.alertDelete(alert.id);
      enqueueSnackbar('Alerta eliminada exitosamente', { variant: 'success' });
      // Increment refresh counter to trigger data refresh
      setRefreshCounter((prev) => prev + 1);
    } catch (error) {
      enqueueSnackbar('Error al eliminar la alerta', { variant: 'error' });
    }
  };

  const alertsData = {
    count: totalAlerts,
    values: alerts,
  };

  return (
    <Box className={classes.conversationTabContainer}>
      <Card>
        <CardContent>
          <AlertsTable
            alerts={alertsData.values}
            headers={headers}
            loading={alertsLoading}
            onView={handleViewClick}
            onEdit={handleEditClick}
            onDeleteConfirm={handleDeleteConfirm}
            extraColumns={extraColumns}
            paginationParameters={paginationParameters}
            setPaginationParameters={setPaginationParameters}
            totalCount={alertsData.count}
          />
        </CardContent>
      </Card>
      <AlertModal
        interval={interval}
        open={alertModalOpen}
        onClose={handleModalClose}
        sectorId={selectedAlert?.sectorId || ''}
        measurements={
          selectedAlert?.chart === 'temperatura-humedad'
            ? [
              { id: 'Temperature', name: 'Temperatura' },
              { id: 'Humidity', name: 'Humedad' },
            ]
            : []
        }
        chartName={selectedAlert?.chart || ''}
        mode={modalMode}
        alertData={selectedAlert ?? undefined}
        onAlertUpdated={() => setRefreshCounter((prev) => prev + 1)}
      />
    </Box>
  );
};

export default AlertsTab;
